import React from 'react';
import styled from '@emotion/styled';
import { Button, TextField } from '@material-ui/core';
import { Check } from '@material-ui/icons';

import { colors, typography } from '@/styles';

// The Figma uses a slate palette (#1e293b / #64748b / #94a3b8 / #e2e8f0 / #f8fafc) that
// isn't in src/styles/colors.js. Rather than introduce eight one-off hexes, each maps to
// the nearest existing token — the drawer then matches every other surface in the app,
// which matters more than an exact hex.
export const DRAWER_WIDTH = 420;

export const Panel = styled.div`
    ${typography.sansSerif}
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 24px 24px;
    width: ${DRAWER_WIDTH}px;
    max-width: 100vw;
    background: ${colors.white.primary};
`;

export const HeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

export const Title = styled.span`
    font-size: 20px;
    font-weight: 800;
    color: ${colors.greys[3]};
`;

export const Subtitle = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 12px;
    font-weight: 400;
    color: ${colors.greys[1]};
`;

export const Divider = styled.hr`
    margin: 0;
    border: none;
    border-top: 1px solid ${colors.greys.border};
`;

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const SectionTitle = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.greys[3]};
`;

export const SectionNote = styled.span`
    font-size: 11px;
    font-weight: 400;
    line-height: 1.4;
    color: ${colors.greys[1]};
`;

export const FieldRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

// Matches the design's inputs: 1px border, 6px radius, 13px text, ~10px/12px padding.
// Built on MUI's outlined TextField so it keeps the app's focus, error and helper-text
// behaviour instead of re-implementing it.
export const CompactField = styled(TextField)`
    & .MuiOutlinedInput-root {
        border-radius: 6px;
        background: ${colors.white.primary};
        font-size: 13px;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-color: ${colors.greys.border};
    }

    & .MuiOutlinedInput-input {
        padding: 10px 12px;
        font-size: 13px;
    }

    & .MuiSelect-root {
        font-weight: 600;
        color: ${colors.greys[3]};
    }

    & .MuiOutlinedInput-input::placeholder {
        color: ${colors.greys[0]};
        opacity: 1;
    }

    & .MuiFormHelperText-root {
        margin: 4px 0 0;
        font-size: 11px;
    }
`;

export const InfoBanner = styled.div`
    padding: 14px;
    border: 1px solid ${colors.greys.border};
    border-radius: 8px;
    background: ${colors.white.secondary};
    font-size: 11px;
    line-height: 1.5;
    color: ${colors.greys[1]};
`;

export const Emphasis = styled.span`
    font-weight: 700;
    color: ${colors.greys[3]};
`;

// The dropzone's own container: dashed border and the 160px height from the design.
// CSVDropzone renders an outlined MUI Paper, so the border is overridden here.
export const DropzoneWrap = styled.div`
    & .dropzone {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 160px;
        padding: 0;
        border: 1.5px dashed ${colors.greys.border};
        border-radius: 8px;
        background: ${colors.white.primary};
        transition: border-color 120ms ease, background-color 120ms ease;
    }

    & .dropzone:hover {
        border-color: ${colors.greens.primary};
        background: ${colors.white.secondary};
    }
`;

export const DropzoneLabel = styled.span`
    display: block;
    font-size: 13px;
    color: ${colors.greys[1]};
`;

// Visual only. react-dropzone's root already opens the file picker on click, so a real
// nested <button> would fire it twice — the click reaches the root by bubbling.
export const BrowsePill = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    margin-top: 8px;
    padding: 0 16px;
    border-radius: 8px;
    background: ${colors.greens.primary};
    color: ${colors.white.primary};
    font-size: 13px;
    font-weight: 500;
`;

// Conflict groups in the Upload Exceptions modal. The design sets these in the gold
// used elsewhere for "needs attention".
export const ConflictGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 4px;
`;

export const ExistingRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 700;
    color: ${colors.golds.primary};
    /* The account picked for Connect is tinted so the choice reads at a glance. */
    margin: 0 -8px;
    padding: 4px 8px;
    border-radius: 4px;
    background: ${({ highlighted }) => (highlighted ? colors.golds.highlight : 'transparent')};
`;

// Note under each flagged row saying what will happen to it. Undecided rows are
// called out in gold so the ones still blocking the commit stand out.
export const DecisionNote = styled.span`
    font-size: 13px;
    font-weight: ${({ pending }) => (pending ? 600 : 400)};
    line-height: 1.4;
    color: ${({ pending }) => (pending ? colors.golds.primary : colors.greys[3])};
`;

/**
 * One button style for every decision in the exceptions modal — Connect, Skip and Add
 * Anyway. They used to be three different buttons with three different "selected"
 * looks, which made it hard to tell which one was picked. Now every option is outlined
 * until chosen, and the chosen one is filled with a check.
 */
export const DecisionButton = styled(({ selected, children, ...rest }) => (
    <Button variant="outlined" aria-pressed={!!selected} {...rest}>
        {selected ? <Check style={{ fontSize: 16, marginRight: 4 }} /> : null}
        {children}
    </Button>
))`
    ${typography.sansSerif}
    flex: 0 0 auto;
    min-width: 0;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    color: ${({ selected }) => (selected ? colors.white[1] : colors.greys[3])};
    background-color: ${({ selected }) => (selected ? colors.greens.primary : colors.white[1])};
    border-color: ${({ selected }) => (selected ? colors.greens.primary : colors.greys[2])};

    &.MuiButton-root:hover {
        background-color: ${({ selected }) => (selected ? colors.greens.primary : colors.greys.selected)};
    }
`;

export const ExistingIndex = styled.span`
    flex: 0 0 24px;
    text-align: right;
`;

export const ExistingName = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ExistingDot = styled.span`
    flex: 0 0 auto;
`;

export const ExistingMeta = styled.span`
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 400;
    color: ${colors.greys[1]};
`;
